import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  Plus,
  Ruler,
  Image as ImageIcon,
  WandSparkles
} from 'lucide-react';
import {
  getCustomer,
  getMeasurements,
  getPatterns,
  getPatternRevisions,
  getGarmentTypeConfigs,
  getGarmentSpecificationFields
} from '@/lib/api';

// Import tab components (we'll create these)
import BodyMeasurementsTab from '@/components/fitting_studio/BodyMeasurementsTab';
import PatternTab from '@/components/fitting_studio/PatternTab';
import PhotosTab from '@/components/fitting_studio/PhotosTab';
import PerthImportWizardTab from '@/components/fitting_studio/PerthImportWizardTab';

const GARMENT_TYPES = [
  { key: 'jacket', label: 'Jacket', prefix: 'J', color: 'bg-blue-500', icon: '🧥' },
  { key: 'trouser', label: 'Trouser', prefix: 'T', color: 'bg-green-500', icon: '👖' },
  { key: 'shirt', label: 'Shirt', prefix: 'S', color: 'bg-purple-500', icon: '👔' },
  { key: 'waistcoat', label: 'Waistcoat', prefix: 'W', color: 'bg-amber-500', icon: '🦺' },
  { key: 'overcoat', label: 'Overcoat', prefix: 'O', color: 'bg-slate-500', icon: '🧥' },
];

const UnifiedFittingStudioPage = () => {
  const { customerId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [customer, setCustomer] = useState(null);
  const [measurements, setMeasurements] = useState([]);
  const [patternsByType, setPatternsByType] = useState({});
  const [revisionsByPattern, setRevisionsByPattern] = useState({});
  const [garmentConfigs, setGarmentConfigs] = useState([]);
  const [specFieldsByType, setSpecFieldsByType] = useState({});
  const [loading, setLoading] = useState(true);
  const [patternHeaderConfig, setPatternHeaderConfig] = useState(null);
  
  const defaultTabFromQuery = searchParams.get('tab');
  const initialTab = defaultTabFromQuery && ['body', 'photos', 'perth-import-wizard', ...GARMENT_TYPES.map((g) => g.key)].includes(defaultTabFromQuery)
    ? defaultTabFromQuery
    : 'body';
  const [activeTab, setActiveTab] = useState(initialTab);

  const isPatternTab = GARMENT_TYPES.some((type) => type.key === activeTab);

  useEffect(() => {
    if (!isPatternTab) {
      setPatternHeaderConfig(null);
    }
  }, [isPatternTab]);

  useEffect(() => {
    fetchAllData();
  }, [customerId]);

  const fetchAllData = async () => {
    try {
      setLoading(true);
      
      // Fetch initial data in parallel
      const [customerRes, measurementsRes, patternsRes, configsRes] = await Promise.all([
        getCustomer(customerId),
        getMeasurements(customerId),
        getPatterns(customerId),
        getGarmentTypeConfigs()
      ]);

      setCustomer(customerRes.data);
      
      // Sort measurements by date (most recent first)
      const sortedMeasurements = measurementsRes.data.sort((a, b) => 
        new Date(b.measurement_date) - new Date(a.measurement_date)
      );
      setMeasurements(sortedMeasurements);
      
      const patterns = patternsRes.data;
      
      // Group patterns by garment type
      const grouped = {};
      for (const type of GARMENT_TYPES) {
        grouped[type.key] = patterns.filter(p => p.garment_type === type.key);
      }
      setPatternsByType(grouped);

      // Fetch revisions for all patterns IN PARALLEL
      const revisionPromises = patterns.map(pattern => 
        getPatternRevisions(pattern.pattern_id)
          .then(res => ({
            pattern_id: pattern.pattern_id,
            revisions: res.data.sort((a, b) => b.revision_number - a.revision_number)
          }))
      );
      
      // Fetch specification fields for each garment type IN PARALLEL
      const specPromises = configsRes.data.map(config =>
        getGarmentSpecificationFields(config.config_id)
          .then(res => ({
            garment_type: config.garment_type,
            fields: res.data.sort((a, b) => a.display_order - b.display_order)
          }))
      );

      // Wait for all parallel calls to complete
      const [revisionResults, specResults] = await Promise.all([
        Promise.all(revisionPromises),
        Promise.all(specPromises)
      ]);

      // Convert results to maps
      const revisionsMap = {};
      revisionResults.forEach(result => {
        revisionsMap[result.pattern_id] = result.revisions;
      });
      setRevisionsByPattern(revisionsMap);

      const specsMap = {};
      specResults.forEach(result => {
        specsMap[result.garment_type] = result.fields;
      });
      setSpecFieldsByType(specsMap);
      setGarmentConfigs(configsRes.data);

    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load fitting studio data');
    } finally {
      setLoading(false);
    }
  };

  const refreshMeasurementsOnly = async () => {
    try {
      const measurementsRes = await getMeasurements(customerId);
      const sortedMeasurements = measurementsRes.data.sort((a, b) =>
        new Date(b.measurement_date) - new Date(a.measurement_date)
      );
      setMeasurements(sortedMeasurements);
    } catch (error) {
      console.error('Failed to refresh measurements:', error);
      toast.error('Failed to refresh body measurements');
    }
  };

  const refreshPatternsOnly = async ({ silent = true, retryCount = 1 } = {}) => {
    try {
      const patternsRes = await getPatterns(customerId);
      const patterns = patternsRes.data || [];

      const grouped = {};
      for (const type of GARMENT_TYPES) {
        grouped[type.key] = patterns.filter((p) => p.garment_type === type.key);
      }
      setPatternsByType(grouped);

      const revisionPromises = patterns.map((pattern) =>
        getPatternRevisions(pattern.pattern_id).then((res) => ({
          pattern_id: pattern.pattern_id,
          revisions: res.data.sort((a, b) => b.revision_number - a.revision_number),
        }))
      );

      const revisionResults = await Promise.all(revisionPromises);
      const revisionsMap = {};
      revisionResults.forEach((result) => {
        revisionsMap[result.pattern_id] = result.revisions;
      });
      setRevisionsByPattern(revisionsMap);
    } catch (error) {
      console.error('Failed to refresh patterns:', error);
      if (retryCount > 0) {
        setTimeout(() => {
          refreshPatternsOnly({ silent: true, retryCount: retryCount - 1 });
        }, 700);
      } else if (!silent) {
        toast.error('Failed to refresh garment patterns');
      }
    }
  };

  // Keeps the cached revisionsByPattern map in sync the instant a pattern revision is
  // saved (autosave, notes edit, measurement link, version number) so that switching
  // garment-type tabs away and back - which unmounts/remounts PatternTab - re-derives
  // from up-to-date data instead of the stale snapshot from the last full page load.
  const syncRevisionIntoCache = (revisionId, patch) => {
    setRevisionsByPattern((previousMap) => {
      let didUpdate = false;
      const nextMap = { ...previousMap };

      for (const patternId of Object.keys(nextMap)) {
        const revisionIndex = nextMap[patternId]?.findIndex((revision) => revision.revision_id === revisionId);
        if (revisionIndex !== undefined && revisionIndex !== -1) {
          const updatedRevisions = [...nextMap[patternId]];
          updatedRevisions[revisionIndex] = { ...updatedRevisions[revisionIndex], ...patch };
          nextMap[patternId] = updatedRevisions;
          didUpdate = true;
          break;
        }
      }

      return didUpdate ? nextMap : previousMap;
    });
  };

  if (loading) {
    return (
      <div className="h-screen flex flex-col">
        <div className="border-b bg-card shadow-sm p-6">
          <Skeleton className="h-8 w-64" />
        </div>
        <div className="flex-1 p-6">
          <Skeleton className="h-96 w-full" />
        </div>
      </div>
    );
  }

  if (!customer) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <p className="text-lg font-medium">Customer not found</p>
          <Button variant="outline" className="mt-4" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Go Back
          </Button>
        </div>
      </div>
    );
  }

  const headerSaveToneClass = {
    idle: 'border-slate-200 bg-white text-slate-600',
    saving: 'border-sky-200 bg-sky-50 text-sky-700',
    saved: 'border-emerald-200 bg-emerald-50 text-emerald-700',
    error: 'border-red-200 bg-red-50 text-red-700',
  }[patternHeaderConfig?.saveState || 'idle'];

  return (
    <div className="h-screen flex flex-col bg-background">
      {/* Compact Header with Dropdown */}
      <div className="sticky top-0 z-30 border-b bg-card/95 shadow-sm backdrop-blur supports-[backdrop-filter]:bg-card/90" data-testid="fitting-studio-page-header">
        <div className="container mx-auto px-4 sm:px-6 py-2.5">
          <div className="flex items-center gap-4">
            {/* Back button */}
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => navigate(-1)}
              className="gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </Button>
            
            <div className="h-6 w-px bg-border" />
            
            {/* Customer name */}
            <div className="flex min-w-0 items-center gap-2">
              <h1 className="truncate text-base font-semibold">
                Fitting Studio - {customer?.customer_name || customer?.name || 'Loading...'}
              </h1>
              <Badge variant="outline" className="h-6 shrink-0 rounded-full px-2 text-[11px] font-medium" data-testid="fitting-studio-header-meta">
                Client {customer?.customer_no || customer?.customer_number}
              </Badge>
            </div>

            <div className="h-6 w-px bg-border" />

            {/* View Selector Dropdown */}
            <Select value={activeTab} onValueChange={setActiveTab}>
              <SelectTrigger className="h-9 w-[220px]" data-testid="fitting-studio-tab-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="body">
                  <div className="flex items-center gap-2">
                    <Ruler className="w-4 h-4" />
                    <span>Body Measurements</span>
                  </div>
                </SelectItem>
                {GARMENT_TYPES.map(type => {
                  // Count patterns with at least one revision
                  const patternsWithRevisions = patternsByType[type.key]?.filter(pattern => {
                    const revisions = revisionsByPattern[pattern.pattern_id] || [];
                    return revisions.length > 0;
                  }) || [];
                  
                  return (
                    <SelectItem key={type.key} value={type.key}>
                      <div className="flex items-center gap-2">
                        <span>{type.icon}</span>
                        <span>{type.label}</span>
                        {patternsWithRevisions.length > 0 && (
                          <Badge variant="secondary" className="ml-1 text-xs">
                            {patternsWithRevisions.length}
                          </Badge>
                        )}
                      </div>
                    </SelectItem>
                  );
                })}
                <SelectItem value="photos">
                  <div className="flex items-center gap-2">
                    <ImageIcon className="w-4 h-4" />
                    <span>Photos</span>
                  </div>
                </SelectItem>
                <SelectItem value="perth-import-wizard" data-testid="fitting-studio-perth-import-wizard-tab-option">
                  <div className="flex items-center gap-2">
                    <WandSparkles className="w-4 h-4" />
                    <span>Perth Import Wizard</span>
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>

            {patternHeaderConfig && (
              <div className="ml-auto flex items-center gap-2" data-testid="pattern-workspace-toolbar">
                <Button
                  size="sm"
                  className="h-8"
                  onClick={patternHeaderConfig.onNewRevision}
                  data-testid="pattern-toolbar-new-revision-button"
                >
                  + New Revision
                </Button>
                <div
                  className={`inline-flex h-8 items-center gap-2 rounded-full border px-3 text-xs font-medium ${headerSaveToneClass}`}
                  data-testid="pattern-toolbar-save-status"
                >
                  <span>{patternHeaderConfig.saveMessage}</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Content Area - Full Height */}
      <div className="flex-1 overflow-auto">
        <div
          className={activeTab === 'perth-import-wizard'
            ? 'px-4 sm:px-6 py-4'
            : 'container mx-auto px-4 sm:px-6 py-4'}
          data-testid="fitting-studio-content-container"
        >
          {activeTab === 'body' && (
            <BodyMeasurementsTab 
              customerId={customerId}
              customer={customer}
              measurements={measurements}
              onRefresh={fetchAllData}
            />
          )}

          {GARMENT_TYPES.map(type => (
            activeTab === type.key && (
              <PatternTab
                key={type.key}
                customerId={customerId}
                customer={customer}
                garmentType={type}
                patterns={patternsByType[type.key] || []}
                revisionsByPattern={revisionsByPattern}
                specFields={specFieldsByType[type.key] || []}
                allMeasurements={measurements}
                onRefresh={fetchAllData}
                onRevisionSynced={syncRevisionIntoCache}
                onHeaderConfigChange={setPatternHeaderConfig}
              />
            )
          ))}

          {activeTab === 'photos' && (
            <PhotosTab
              customerId={customerId}
              customer={customer}
              patterns={Object.values(patternsByType).flat()}
              onRefresh={fetchAllData}
            />
          )}

          {activeTab === 'perth-import-wizard' && (
            <PerthImportWizardTab
              customerId={customerId}
              customer={customer}
              onRefresh={fetchAllData}
              onMeasurementsUpdated={refreshMeasurementsOnly}
              onPatternsUpdated={refreshPatternsOnly}
              measurements={measurements}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default UnifiedFittingStudioPage;
